import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * @typedef {Object} CartLineItemProperty
 * @property {string} name
 * @property {string} value
 */

/**
 * @typedef {Object} CartLineItem
 * @property {string} key
 * @property {number} quantity
 * @property {Record<string, string> | CartLineItemProperty[]} [properties]
 */

/**
 * @typedef {Object} CartAjaxResponse
 * @property {CartLineItem[]} [items]
 * @property {Record<string, string>} [sections]
 * @property {string} [errors]
 * @property {string} [message]
 */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getCartRowByLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Adds a free gift line item linked to a parent cart line.
   * @param {{ variantId: number, parentLineKey: string }} data
   * @param {Event} event
   */
  onFreeProductAdd(data, event) {
    const trigger = event.target instanceof Element ? event.target : null;
    const freeGroup = trigger?.closest('.cart-free-products__group');

    if (freeGroup instanceof HTMLElement) {
      const remaining = Number(freeGroup.dataset.freeGiftsRemaining || 0);
      if (remaining <= 0) return;
    }

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('add-free-gift:user-action');

    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    const body = JSON.stringify({
      items: [
        {
          id: data.variantId,
          quantity: 1,
          properties: {
            _free_gift: 'true',
            _parent_line_key: data.parentLineKey,
          },
        },
      ],
      sections: Array.from(this.#getSectionsToUpdate()).join(','),
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then((parsedResponse) => {
        resetShimmer(this);

        if (parsedResponse.status) {
          console.error(parsedResponse.description || parsedResponse.message);
          return;
        }

        this.#applyCartSections(parsedResponse);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Removes only the free gift linked to a parent line.
   * @param {{ lineKey: string }} data
   */
  onFreeGiftRemove(data) {
    this.#updateCartByKeys({ [data.lineKey]: 0 }, 'clear-free-gift');
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getCartRowByLine(line);

    if (!cartItemRowToRemove) return;

    const parentKey = cartItemRowToRemove.dataset.lineItemKey;
    const childKeys = this.#parseChildLineKeys(cartItemRowToRemove.dataset.childLineKeys);

    if (parentKey && childKeys.length > 0) {
      /** @type {Record<string, number>} */
      const updates = { [parentKey]: 0 };
      childKeys.forEach((key) => {
        updates[key] = 0;
      });
      this.#updateCartByKeys(updates, 'clear', cartItemRowToRemove);
      return;
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    cartTotal?.shimmer();

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(this.#getSectionsToUpdate()).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const trimResult = await this.#trimExcessFreeGiftsForLine(line, parsedResponseText);
        await this.#applyCartSectionsAfterQuantityChange(parsedResponseText, trimResult);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * @param {Record<string, number>} updates
   * @param {string} action
   * @param {HTMLTableRowElement} [rowToAnimate]
   * @returns {Promise<Object | null>}
   */
  async #updateCartByKeys(updates, action, rowToAnimate) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);

    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    const body = JSON.stringify({
      updates,
      sections: Array.from(this.#getSectionsToUpdate()).join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const parsedResponse = await response.json();

      resetShimmer(this);

      if (parsedResponse.errors || parsedResponse.message) {
        console.error(parsedResponse.errors || parsedResponse.message);
        return null;
      }

      if (rowToAnimate) {
        const apply = () => {
          this.#applyCartSections(parsedResponse);
        };

        if (prefersReducedMotion()) {
          apply();
          return parsedResponse;
        }

        rowToAnimate.style.setProperty('--row-height', `${rowToAnimate.clientHeight}px`);
        rowToAnimate.classList.add('removing');
        onAnimationEnd(rowToAnimate, apply);
        return parsedResponse;
      }

      this.#applyCartSections(parsedResponse);
      return parsedResponse;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Removes excess free gifts when parent quantity is reduced.
   * @param {number} line
   * @param {CartAjaxResponse} [cartFromChange]
   * @returns {Promise<{ trimmed: boolean, response?: CartAjaxResponse }>}
   */
  async #trimExcessFreeGiftsForLine(line, cartFromChange) {
    const row = this.#getCartRowByLine(line);
    if (!row || row.classList.contains('cart-items__table-row--free-gift')) {
      return { trimmed: false };
    }

    const parentKey = row.dataset.lineItemKey;
    if (!parentKey) return { trimmed: false };

    const childKeysFromDom = this.#parseChildLineKeys(row.dataset.childLineKeys);

    try {
      /** @type {CartLineItem[]} */
      let items = cartFromChange?.items ?? [];

      if (items.length === 0) {
        const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
        const cart = await cartResponse.json();
        items = cart.items ?? [];
      }

      const parentItem = items.find((item) => item.key === parentKey);
      if (!parentItem) return { trimmed: false };

      const childItems = this.#getChildGiftItems(items, parentKey, childKeysFromDom);
      const childGiftQty = childItems.reduce((sum, item) => sum + item.quantity, 0);
      let excessQty = childGiftQty - parentItem.quantity;

      if (excessQty <= 0) return { trimmed: false };

      /** @type {Record<string, number>} */
      const updates = {};
      for (const item of [...childItems].reverse()) {
        if (excessQty <= 0) break;

        if (item.quantity <= excessQty) {
          updates[item.key] = 0;
          excessQty -= item.quantity;
        } else {
          updates[item.key] = item.quantity - excessQty;
          excessQty = 0;
        }
      }

      if (Object.keys(updates).length === 0) return { trimmed: false };

      const body = JSON.stringify({
        updates,
        sections: Array.from(this.#getSectionsToUpdate()).join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const parsed = /** @type {CartAjaxResponse} */ (await response.json());

      if (parsed.errors || parsed.message) {
        console.error(parsed.errors || parsed.message);
        return { trimmed: false };
      }

      return { trimmed: true, response: parsed };
    } catch (error) {
      console.error(error);
      return { trimmed: false };
    }
  }

  /**
   * @param {CartLineItem} item
   * @param {string} name
   * @returns {string | undefined}
   */
  #getLineProperty(item, name) {
    const properties = item.properties;
    if (!properties) return undefined;

    if (Array.isArray(properties)) {
      const entry = properties.find((property) => property.name === name);
      return entry?.value;
    }

    if (typeof properties === 'object') {
      return properties[name];
    }

    return undefined;
  }

  /**
   * @param {CartLineItem[]} items
   * @param {string} parentKey
   * @param {string[]} childKeysFromDom
   * @returns {CartLineItem[]}
   */
  #getChildGiftItems(items, parentKey, childKeysFromDom) {
    const childKeySet = new Set(childKeysFromDom);

    return items.filter((item) => {
      if (childKeySet.has(item.key)) return true;
      return this.#getLineProperty(item, '_parent_line_key') === parentKey;
    });
  }

  /**
   * @param {CartAjaxResponse} changeResponse
   * @param {{ trimmed: boolean, response?: CartAjaxResponse }} trimResult
   */
  async #applyCartSectionsAfterQuantityChange(changeResponse, trimResult) {
    if (trimResult.trimmed) {
      if (trimResult.response?.sections?.[this.sectionId]) {
        this.#applyCartSections(trimResult.response);
        return;
      }

      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    this.#applyCartSections(changeResponse);
  }

  /**
   * @param {string | undefined} keys
   * @returns {string[]}
   */
  #parseChildLineKeys(keys) {
    if (!keys) return [];
    return keys.split(',').map((key) => key.trim()).filter(Boolean);
  }

  /**
   * @param {number} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #getCartRowByLine(line) {
    return this.refs.cartItemRows?.find(
      (row) => row instanceof HTMLElement && row.dataset.cartLine === String(line)
    );
  }

  /**
   * @param {number} line
   * @returns {HTMLElement | null | undefined}
   */
  #getQuantitySelectorByLine(line) {
    const input = this.querySelector(`input[data-cart-line="${line}"]`);
    return input?.closest('quantity-selector-component') ?? undefined;
  }

  /**
   * @returns {Set<string>}
   */
  #getSectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return sectionsToUpdate;
  }

  /**
   * @param {CartAjaxResponse} parsedResponse
   */
  #applyCartSections(parsedResponse) {
    const sections = parsedResponse.sections;
    if (!sections?.[this.sectionId]) return;

    const newSectionHTML = new DOMParser().parseFromString(sections[this.sectionId], 'text/html');
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections,
      })
    );

    morphSection(this.sectionId, sections[this.sectionId]);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getQuantitySelectorByLine(line);
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
